import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  HostListener,
  inject,
  OnInit,
  signal,
  untracked,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  trigger,
  state,
  style,
  transition,
  animate,
} from '@angular/animations';
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  RouterModule,
} from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { SelectModule } from 'primeng/select';
import { IMenuItem } from './menu.interface';
import { FormsModule } from '@angular/forms';
import { filter } from 'rxjs';
import { Breadcrumb, Fullscreen, User } from '../components';
import { ButtonModule } from 'primeng/button';
import { Language } from '../components/language/language';
import { EnumPermissions } from '../constants';
import { UserService } from '../services';
import {
  doesUserHasAllPermissions,
  doesUserHasPermission,
} from '../utils/permission';
import { IRole } from '../model/role';
import { LandingThemeService } from '../theme/landing-theme.service';
import { ThemeSwitcherComponent } from '../theme/theme-switcher.component';
import { LANDING_THEME_PREVIEW } from '../utils/di.tokens';

@Component({
  selector: 'lib-layout',
  imports: [
    CommonModule,
    TranslocoPipe,
    SelectModule,
    RouterModule,
    FormsModule,
    Breadcrumb,
    ButtonModule,
    Language,
    Fullscreen,
    User,
    ThemeSwitcherComponent,
  ],
  providers: [{ provide: LANDING_THEME_PREVIEW, useValue: true }],
  templateUrl: './layout.html',
  styleUrl: './layout.css',
  animations: [
    trigger('rotateAnimation', [
      state('default', style({ transform: 'rotate(0deg)' })),
      state('rotated', style({ transform: 'rotate(90deg)' })),
      transition('default <=> rotated', [animate('300ms ease-in-out')]),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Layout implements OnInit {
  permissions = EnumPermissions;
  readonly themeService = inject(LandingThemeService);
  private readonly userService = inject(UserService);

  @HostListener('window:resize', [])
  onResize() {
    this.checkIsMobile();
  }

  $router = inject(Router);

  $activatedRoute = inject(ActivatedRoute);

  menuItems = signal<IMenuItem[]>([]);
  visibleMenuItems = computed(() => {
    const user = this.userService.user();
    const isVisible = (item: IMenuItem) => !item.isVisible || item.isVisible(user);
    const byUser = (items: IMenuItem[]): IMenuItem[] =>
      items
        .filter(isVisible)
        .map((item) =>
          item.children?.length
            ? { ...item, children: item.children.filter(isVisible) }
            : item,
        );
    return this.filterMenuItemsByPermission(
      byUser(this.menuItems()),
      user?.roles,
    );
  });

  isMobile = signal(false);

  isMobileHide = signal(false);

  activePathName = signal('');

  constructor() {
    // Profil (ruxsatlar) keyinroq yuklanadi va menyu ro'yxati o'zgaradi — ochiq guruhni qayta aniqlaymiz.
    effect(() => {
      this.visibleMenuItems();
      untracked(() => this.syncActiveSubmenuByCurrentRoute());
    });
  }

  checkIsMobile(): void {
    this.isMobile.set(window.innerWidth <= 960);
    if (!this.isMobile()) {
      this.isMobileHide.set(false);
    } else {
      this.isSidebarOpen.set(true);
    }
  }

  ngOnInit(): void {
    this.checkIsMobile();

    this.getPathNameByString();

    this.listenRouteChange();

    this.menuItems.set(this.$activatedRoute.snapshot.data['menuItems'] ?? []);
    this.syncActiveSubmenuByCurrentRoute();
  }

  listenRouteChange() {
    this.$router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.getPathNameByString();
        this.syncActiveSubmenuByCurrentRoute();
      });
  }

  /** Ichki sahifalarda ham (masalan `/exam-seating/209`) tegishli submenyu ochiq qoladi; eng uzun mos havola tanlanadi. */
  private syncActiveSubmenuByCurrentRoute(): void {
    const path = this.$router.url.split(/[?#]/)[0];
    let bestIndex = -1;
    let bestLength = -1;

    this.visibleMenuItems().forEach((item, index) => {
      for (const child of item.children || []) {
        const link = child.link?.split(/[?#]/)[0];
        if (!link) {
          continue;
        }
        const matches = child.exact
          ? path === link
          : path === link || path.startsWith(link.endsWith('/') ? link : `${link}/`);
        if (matches && link.length > bestLength) {
          bestIndex = index;
          bestLength = link.length;
        }
      }
    });

    this.activeSubmenuIndex.set(bestIndex);
  }

  private filterMenuItemsByPermission(
    menuItems: IMenuItem[],
    userRoles: IRole[] | undefined,
  ): IMenuItem[] {
    return menuItems
      .map((item) => {
        if (!item.children?.length) {
          return this.isAllowed(
            item.permissionIds,
            userRoles,
            item.roleIds,
            item.requiredPermissionIds,
          )
            ? item
            : null;
        }

        const allowedChildren = item.children.filter((child) =>
          this.isAllowed(
            child.permissionIds,
            userRoles,
            child.roleIds,
            child.requiredPermissionIds,
          ),
        );

        const isParentAllowed = this.isAllowed(
          item.permissionIds,
          userRoles,
          item.roleIds,
          item.requiredPermissionIds,
        );
        if (!isParentAllowed || !allowedChildren.length) {
          return null;
        }

        return {
          ...item,
          children: allowedChildren,
        };
      })
      .filter((item): item is IMenuItem => item !== null);
  }

  private isAllowed(
    permissionIds: string[] | undefined,
    userRoles: IRole[] | undefined,
    roleIds?: number[],
    requiredPermissionIds?: string[],
  ): boolean {
    if (!userRoles?.length) {
      return (
        !permissionIds?.length &&
        !roleIds?.length &&
        !requiredPermissionIds?.length
      );
    }

    const hasPermission =
      !permissionIds?.length || doesUserHasPermission(userRoles, permissionIds);
    const hasRequiredPermissions =
      !requiredPermissionIds?.length ||
      doesUserHasAllPermissions(userRoles, requiredPermissionIds);

    const hasRole =
      !roleIds?.length ||
      userRoles.some((role) => !!role.id && roleIds.includes(role.id));

    return hasPermission && hasRequiredPermissions && hasRole;
  }

  private getPrimaryPathSegments(): string[] {
    return (
      this.$router.parseUrl(this.$router.url).root.children['primary']?.segments.map(
        (segment) => segment.path,
      ) ?? []
    );
  }

  getPathNameByString() {
    const titleFromRoute = this.getTitleKeyFromRouteData();
    if (titleFromRoute) {
      this.activePathName.set(titleFromRoute);
      return;
    }

    const segments = this.getPrimaryPathSegments();
    const last = segments[segments.length - 1];
    const prev = segments[segments.length - 2];
    const lastSegment = /^\d+$/.test(last ?? '') ? prev : last;
    this.activePathName.set(lastSegment ?? '');
  }

  private getTitleKeyFromRouteData(): string {
    let currentRoute = this.$activatedRoute.root;
    let titleKey = '';

    while (currentRoute.children.length > 0) {
      const child = currentRoute.children[0];
      const breadcrumb = child.snapshot.data?.['breadcrumb'];
      if (typeof breadcrumb === 'string' && breadcrumb.length) {
        titleKey = breadcrumb;
      }
      currentRoute = child;
    }

    return titleKey;
  }

  shouldShowBreadcrumb(): boolean {
    const segments = this.getPrimaryPathSegments();
    return !(
      segments[0] === 'checking-expert' &&
      segments[1] === 'expert-checking-detail'
    );
  }

  isSidebarOpen = signal(true);

  activeSubmenuIndex = signal(-1);

  setActiveSubmenu(index: number) {
    if (this.activeSubmenuIndex() == index) {
      this.activeSubmenuIndex.set(-1);
      return;
    }

    this.activeSubmenuIndex.set(index);
  }
  sideBarClose() {
    if (this.isMobile()) {
      this.isMobileHide.set(!this.isMobileHide());
      return;
    }
    this.isSidebarOpen.set(!this.isSidebarOpen());
  }
}
